'use client'

import { ChangeEvent, useEffect, useRef, useState } from 'react'
import {
  Button,
  Cell,
  Dialog,
  Divider,
  InfoBanner,
  InputTextArea,
  Navigation,
  SelectionGroup,
  Text,
  Validation,
} from '@vinted/web-ui'
import { X24 } from '@vinted/monochrome-icons'

import { useTracking } from '@marketplace-web/observability/event-tracker-data'
import { useTranslate } from '@marketplace-web/i18n/i18n-feature'
import { ScrollableArea } from '@marketplace-web/common-components/scrollable-area-ui'
import { ContentLoader } from '@marketplace-web/common-components/content-loader-ui'
import { useConversationNavigator } from '@marketplace-web/messaging/navigation-feature'
import {
  DeadlineExtensionOptionsModel,
  transformDeadlineExtensionOptionsDto,
  getDeadlineExtensionOptions,
  createShippingDeadlineExtension,
  viewEvent,
} from '@marketplace-web/conversation-shipping/conversation-shipping-data'
import { useConversationContext } from '@marketplace-web/messaging/conversation-providers-feature'

import ShipmentItems from './ShipmentItems'

type Props = {
  isOpen: boolean
  onClose: () => void
}

const MAX_LENGTH_CHARS = 200

const ExtendShippingDeadlineModal = ({ isOpen, onClose }: Props) => {
  const { track } = useTracking()
  const { conversation } = useConversationContext()

  const conversationNavigator = useConversationNavigator()

  const shipmentId = conversation?.transaction?.shipment?.id
  const transactionId = conversation?.transaction?.id
  const conversationId = conversation?.id

  const translate = useTranslate('conversation.extend_shipping_deadline.modal')

  const [message, setMessage] = useState('')
  const [error, setError] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [selectedDeadlineExtension, setSelectedDeadlineExtension] = useState('')
  const [deadlineExtensionOptions, setDeadlineExtensionOptions] =
    useState<DeadlineExtensionOptionsModel>()

  const inputRef = useRef<HTMLTextAreaElement>(null)

  useEffect(() => {
    if (!isOpen || !transactionId) return

    track(
      viewEvent({
        screen: 'shipping_deadline_extension',
        target: 'extend_shipping_deadline',
        targetDetails: JSON.stringify({
          transaction_id: transactionId,
        }),
      }),
    )
  }, [isOpen, track, transactionId])

  useEffect(() => {
    const fetchDeadlineExtensionOptions = async () => {
      if (!isOpen) return
      if (!shipmentId) return

      setIsLoading(true)

      const response = await getDeadlineExtensionOptions(shipmentId)

      if ('errors' in response) {
        setIsLoading(false)

        return
      }

      const { deadline_extension_options } = response

      const options = transformDeadlineExtensionOptionsDto(deadline_extension_options)

      setIsLoading(false)
      setDeadlineExtensionOptions(options)

      const firstSelectedOption = options.deadlineOptions[0]!.key

      setSelectedDeadlineExtension(firstSelectedOption)
    }

    fetchDeadlineExtensionOptions()
  }, [shipmentId, isOpen])

  const handleSubmit = async () => {
    const navigationStartMark = conversationNavigator.createNavigationStartMark()

    if (!shipmentId || !selectedDeadlineExtension) return

    const response = await createShippingDeadlineExtension({
      shipmentId,
      key: selectedDeadlineExtension,
      reason: message,
    })

    if ('errors' in response) {
      setError(response.errors[0]!.value)

      return
    }

    onClose()

    if (!conversationId) return

    conversationNavigator.navigate(conversationId, { navigationStartMark })
  }

  const handleShippingDeadlineSelection = (key: string) => () => {
    setSelectedDeadlineExtension(key)
  }

  const validateInput = (inputValue: string): string => {
    const inputValueLength = inputValue.trim().length

    if (inputValueLength > MAX_LENGTH_CHARS) {
      const charactersOverTheLimit = { count: inputValueLength - MAX_LENGTH_CHARS }

      return translate('errors.message_too_long', charactersOverTheLimit, charactersOverTheLimit)
    }

    return ''
  }

  const renderValidation = () => {
    if (error) return <Validation text={error} theme="warning" />

    const remainingCharacterCount = { count: MAX_LENGTH_CHARS - message.length }

    return translate('hint', remainingCharacterCount, remainingCharacterCount)
  }

  const handleInputChange = (event: ChangeEvent<HTMLTextAreaElement>) => {
    const { value: inputValue } = event.target

    setError(validateInput(inputValue))
    setMessage(inputValue)
  }

  const handleClose = () => {
    setError('')
    setMessage('')
    onClose()
  }

  const renderNavigation = () => (
    <>
      <Navigation
        body={translate('modal_title')}
        right={
          <Button
            styling="flat"
            onClick={handleClose}
            iconName={X24}
            inline
            testId="extend-shipping-deadline-modal-close"
          />
        }
      />
      <Divider />
    </>
  )

  const renderShipmentItems = () => {
    if (!deadlineExtensionOptions) return null

    return (
      <ShipmentItems
        shipmentItems={deadlineExtensionOptions.items}
        currentShippingDeadline={deadlineExtensionOptions.currentShippingDeadline}
      />
    )
  }

  const renderInfoBanner = () => (
    <InfoBanner body={<Text as="span" text={translate('info_banner_message')} />} type="info" />
  )

  const renderTitle = () => (
    <Cell>
      <Text as="h1" type="heading" text={translate('title')} />
    </Cell>
  )

  const renderDeadlineOptions = () => {
    return (
      <SelectionGroup layout="default">
        {deadlineExtensionOptions?.deadlineOptions.map(option => (
          <SelectionGroup.SelectionItem
            key={option.key}
            title={option.title}
            body={translate('ship_before', {
              date: option.newShippingDeadline,
            })}
            onClick={handleShippingDeadlineSelection(option.key)}
            isSelected={selectedDeadlineExtension === option.key}
            testId={`extend-shipping-deadline-modal-option-${option.key}`}
          />
        ))}
      </SelectionGroup>
    )
  }

  const renderMessage = () => (
    <InputTextArea
      name="message"
      placeholder={translate('message_placeholder')}
      title={translate('message_title')}
      onChange={handleInputChange}
      validation={renderValidation()}
      ref={inputRef}
      testId="extend-shipping-deadline-modal-message"
    />
  )

  const renderSubmit = () => {
    const hasError = !!validateInput(message)

    return (
      <Cell>
        <Button
          styling="filled"
          text={translate('actions.request_for_extension')}
          testId="extend-shipping-deadline-modal-submit-button"
          onClick={handleSubmit}
          disabled={hasError}
        />
      </Cell>
    )
  }

  function renderModalContent() {
    if (isLoading) return <ContentLoader testId="extend-shippin-deadline-modal-loader" />

    return (
      <ScrollableArea>
        {renderNavigation()}
        {renderShipmentItems()}
        {renderInfoBanner()}
        {renderTitle()}
        {renderDeadlineOptions()}
        {renderMessage()}
        {renderSubmit()}
      </ScrollableArea>
    )
  }

  return (
    <Dialog
      hasScrollableContent
      className="u-flexbox"
      show={isOpen}
      testId="extend-shipping-deadline-modal"
    >
      {renderModalContent()}
    </Dialog>
  )
}

export default ExtendShippingDeadlineModal
